<template>
  <section class="sp-panel sp-reader">
    <button class="sp-link" type="button" @click="close">
      ← Back to reviews
    </button>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!review"
      :success="success"
      @retry="load"
    />
    <template v-if="review">
      <div class="sp-row sp-between">
        <div>
          <p class="sp-eyebrow">{{ review.templateTitle }}</p>
          <h2>{{ review.employeeName }}</h2>
        </div>
        <span class="sp-badge">{{ statusLabel(review.status) }}</span>
      </div>
      <dl class="sp-metadata">
        <div>
          <dt>Review period</dt>
          <dd>
            {{ displayDate(review.periodStart) }} –
            {{ displayDate(review.periodEnd) }}
          </dd>
        </div>
        <div>
          <dt>Supervisor</dt>
          <dd>{{ review.reviewerName }}</dd>
        </div>
        <div>
          <dt>Self review due</dt>
          <dd>{{ displayDate(review.dueDate) }}</dd>
        </div>
        <div v-if="review.jobTitle">
          <dt>Position</dt>
          <dd>{{ review.jobTitle }}</dd>
        </div>
        <div v-if="review.department">
          <dt>Department</dt>
          <dd>{{ review.department }}</dd>
        </div>
        <div v-if="review.followUpDueDate">
          <dt>Follow-up meeting target</dt>
          <dd>{{ displayDate(review.followUpDueDate) }}</dd>
        </div>
        <div v-if="review.meetingDate">
          <dt>Meeting date</dt>
          <dd>{{ displayDate(review.meetingDate) }}</dd>
        </div>
      </dl>
      <p v-if="!review.isParticipant" class="sp-notice">
        You can manage this review's assignment and dates shown above. Review
        responses and signatures are private to the employee and assigned
        supervisor.
      </p>
      <template v-if="review.isParticipant && review.template">
        <p v-if="review.cancelReason" class="sp-notice sp-prose">
          Cancellation reason: {{ review.cancelReason }}
        </p>
        <div class="sp-notice">
          <p class="sp-prose">{{ instructions }}</p>
          <p v-if="can('save_self')">
            Your draft is private until you submit it to your supervisor.
          </p>
          <p v-if="can('save_manager')">
            Your comments remain private until you publish the review after your
            meeting.
          </p>
        </div>
        <div
          v-for="(question, index) in review.template.questions"
          :key="question.id"
          class="sp-question"
        >
          <p class="sp-eyebrow">
            {{ index + 1 }} of {{ review.template.questions.length }} ·
            {{ groupLabel(question.group) }}
          </p>
          <h3>{{ question.title }}</h3>
          <p class="sp-prose">{{ question.prompt }}</p>
          <div class="sp-grid">
            <label v-if="can('save_self')" class="sp-field"
              >{{ question.employeeLabel || 'Employee Response'
              }}<textarea
                v-model="answers[question.id]"
                :data-question="question.id"
                maxlength="5000"
                rows="5"
                :disabled="busy"
                :required="!question.optionalPriorPlan"
                @input="dirty = true"
              /><small
                >{{ (answers[question.id] || '').length }}/5000 characters{{
                  question.optionalPriorPlan
                    ? ' · May be left blank if no prior plan applies.'
                    : ''
                }}</small
              ></label
            >
            <div v-else class="sp-answer">
              <h3>{{ question.employeeLabel || 'Employee Response' }}</h3>
              <p class="sp-prose">
                {{
                  review.employeeAnswers
                    ? review.employeeAnswers[question.id] || 'Not provided.'
                    : 'The employee has not submitted their self review yet.'
                }}
              </p>
            </div>
            <label v-if="can('save_manager')" class="sp-field"
              >{{ question.managerLabel || 'Supervisor Comments'
              }}<textarea
                v-model="answers[question.id]"
                :data-question="question.id"
                maxlength="5000"
                rows="5"
                :disabled="busy"
                @input="dirty = true"
              /><small
                >{{ (answers[question.id] || '').length }}/5000
                characters</small
              ></label
            >
            <div v-else-if="showManagerAnswers" class="sp-answer">
              <h3>{{ question.managerLabel || 'Supervisor Comments' }}</h3>
              <p class="sp-prose">
                {{ review.managerAnswers[question.id] || 'Not provided.' }}
              </p>
            </div>
          </div>
        </div>
        <p v-if="!can('save_manager') && !showManagerAnswers" class="sp-notice">
          Supervisor comments will appear here after the supervisor publishes
          the review.
        </p>
        <fieldset
          v-if="can('save_manager')"
          :disabled="busy"
          class="sp-question"
        >
          <label class="sp-field"
            >Review meeting date<input
              v-model="meetingDate"
              type="date"
              :max="today"
              @input="dirty = true"
            /><small
              >Enter the actual meeting date. A future date cannot be used to
              publish a completed review.</small
            ></label
          >
          <label class="sp-check"
            ><input v-model="confirmed" type="checkbox" /> I confirm that the
            review meeting has taken place and these comments are complete.
            Publishing records my signature and shares this review with the
            employee.</label
          >
        </fieldset>
        <label v-if="can('submit_self')" class="sp-check"
          ><input v-model="confirmed" type="checkbox" :disabled="busy" /> I am
          ready to submit my responses to my supervisor. I will no longer be
          able to edit them.</label
        >
        <div v-if="can('acknowledge')" class="sp-notice">
          <h3>Acknowledge your review</h3>
          <p>
            Acknowledgment confirms receipt of the review; it does not mean that
            you agree with every comment.
          </p>
          <label class="sp-field"
            >Your acknowledgment comment (optional)<textarea
              v-model="acknowledgmentComment"
              maxlength="5000"
              :disabled="busy"
              @input="dirty = true"
            />
          </label>
          <label class="sp-check"
            ><input v-model="confirmed" type="checkbox" :disabled="busy" /> I
            have received and read this review. Record my acknowledgment and
            signature.</label
          >
          <oxd-button
            label="Acknowledge review"
            display-type="secondary"
            :disabled="!confirmed || busy"
            @click="act('acknowledge')"
          />
        </div>
        <div
          v-if="can('save_self') || can('save_manager')"
          class="sp-actions sp-sticky-actions"
        >
          <oxd-button
            label="Save draft"
            display-type="ghost"
            :disabled="busy"
            @click="act(can('save_self') ? 'save_self' : 'save_manager')"
          />
          <oxd-button
            v-if="can('submit_self')"
            label="Submit to supervisor"
            display-type="secondary"
            :disabled="!confirmed || busy"
            @click="act('submit_self')"
          />
          <oxd-button
            v-if="can('publish')"
            label="Sign and publish review"
            display-type="secondary"
            :disabled="!confirmed || !meetingDate || busy"
            @click="act('publish')"
          />
          <span class="sp-muted" role="status">{{
            busy
              ? 'Saving…'
              : dirty
              ? 'You have unsaved changes.'
              : 'All changes saved.'
          }}</span>
        </div>
        <div
          v-if="review.managerSignedAt || review.employeeAcknowledgedAt"
          class="sp-notice"
        >
          <p v-if="review.managerSignedAt">
            Supervisor signed {{ displayDate(review.managerSignedAt) }}.
          </p>
          <p v-if="review.employeeAcknowledgedAt">
            Employee acknowledged
            {{ displayDate(review.employeeAcknowledgedAt) }}.
          </p>
          <p v-if="review.acknowledgmentComment" class="sp-prose">
            {{ review.acknowledgmentComment }}
          </p>
        </div>
        <details
          v-if="review.history && review.history.length"
          class="sp-question"
        >
          <summary>Review activity</summary>
          <ol class="sp-history">
            <li v-for="(event, index) in review.history" :key="index">
              {{ actionLabel(event.action) }} · {{ event.actorName }} ·
              {{ displayDate(event.at) }}
            </li>
          </ol>
        </details>
      </template>
      <section v-if="can('reassign') || can('cancel')" class="sp-question">
        <h3>Review administration</h3>
        <p class="sp-muted">
          Reassignment is available until the supervisor starts their comments.
        </p>
        <form v-if="can('reassign')" @submit.prevent="act('reassign')">
          <label class="sp-field"
            >Assigned supervisor<select
              v-model.number="reviewerEmpNumber"
              required
              :disabled="busy"
              @change="dirty = true"
            >
              <option value="">Select a supervisor</option>
              <option
                v-for="employee in reviewerOptions"
                :key="employee.empNumber"
                :value="employee.empNumber"
              >
                {{ employee.name }}
              </option>
            </select></label
          ><oxd-button
            type="submit"
            label="Reassign supervisor"
            display-type="ghost"
            :disabled="
              busy ||
              !reviewerEmpNumber ||
              reviewerEmpNumber === review.reviewerEmpNumber
            "
          />
        </form>
        <form
          v-if="can('cancel')"
          class="sp-question"
          @submit.prevent="act('cancel')"
        >
          <label class="sp-field"
            >Reason for cancellation<textarea
              v-model.trim="cancelReason"
              maxlength="5000"
              required
              :disabled="busy"
              @input="dirty = true"
            /></label
          ><oxd-button
            type="submit"
            label="Cancel this review"
            display-type="danger"
            :disabled="busy || !cancelReason"
          />
        </form>
      </section>
      <div class="sp-actions">
        <button type="button" class="sp-link" :disabled="busy" @click="reload">
          Reload latest review
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import {ref, computed, onMounted} from 'vue';
import PortalFeedback from './PortalFeedback.vue';
import {
  portalApi,
  errorMessage,
  displayDate,
  statusLabel,
  useDraftGuard,
} from '../portal';
export default {
  components: {PortalFeedback},
  props: {
    reviewId: {type: Number, required: true},
    employees: {type: Array, default: () => []},
  },
  emits: ['close', 'changed'],
  setup(props, {emit}) {
    const api = portalApi('reviews');
    const review = ref(null),
      answers = ref({}),
      meetingDate = ref(''),
      acknowledgmentComment = ref(''),
      reviewerEmpNumber = ref(''),
      cancelReason = ref('');
    const loading = ref(false),
      busy = ref(false),
      error = ref(''),
      success = ref(''),
      confirmed = ref(false),
      dirty = ref(false);
    const {canLeave} = useDraftGuard(dirty);
    const today = new Date().toISOString().slice(0, 10);
    const can = (action) => Boolean(review.value?.actions?.includes(action));
    const showManagerAnswers = computed(() =>
      Boolean(
        review.value?.isParticipant &&
          (review.value.participantRole === 'reviewer' ||
            ['manager_published', 'acknowledged'].includes(
              review.value.status,
            )) &&
          review.value.managerAnswers,
      ),
    );
    const instructions = computed(
      () =>
        review.value?.template?.instructions?.[
          can('save_manager') || review.value?.participantRole === 'reviewer'
            ? 'supervisor'
            : 'employee'
        ] || '',
    );
    const reviewerOptions = computed(() =>
      props.employees.filter(
        (employee) => employee.empNumber !== review.value?.empNumber,
      ),
    );
    function applyReview(data) {
      review.value = data;
      answers.value = {
        ...(data.actions?.includes('save_manager')
          ? data.managerAnswers
          : data.employeeAnswers),
      };
      meetingDate.value = data.meetingDate || '';
      reviewerEmpNumber.value = data.reviewerEmpNumber;
      confirmed.value = false;
      dirty.value = false;
      acknowledgmentComment.value = '';
      cancelReason.value = '';
    }
    async function load() {
      loading.value = true;
      error.value = '';
      success.value = '';
      review.value = null;
      try {
        applyReview(await api.get(props.reviewId));
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        loading.value = false;
      }
    }
    function reload() {
      if (canLeave()) {
        dirty.value = false;
        load();
      }
    }
    function close() {
      if (!busy.value && canLeave()) {
        dirty.value = false;
        emit('close');
      }
    }
    function groupLabel(group) {
      return String(group || '').replace(/_/g, ' ');
    }
    function actionLabel(action) {
      return (
        {
          created: 'Review created',
          save_self: 'Employee draft saved',
          submit_self: 'Self review submitted',
          save_manager: 'Supervisor draft saved',
          publish: 'Review published',
          acknowledge: 'Review acknowledged',
          reassign: 'Supervisor reassigned',
          cancel: 'Review cancelled',
        }[action] || groupLabel(action)
      );
    }
    async function act(action) {
      if (busy.value || !can(action)) return;
      error.value = '';
      success.value = '';
      const payload = {action, version: review.value.version};
      if (
        ['save_self', 'submit_self', 'save_manager', 'publish'].includes(action)
      ) {
        if (!review.value.isParticipant) return;
        const questions = review.value.template.questions;
        payload.answers = Object.fromEntries(
          questions.map((question) => [
            question.id,
            answers.value[question.id] || '',
          ]),
        );
        if (['submit_self', 'publish'].includes(action)) {
          if (!confirmed.value) return;
          const missing = questions.find(
            (question) =>
              !payload.answers[question.id].trim() &&
              !(action === 'submit_self' && question.optionalPriorPlan),
          );
          if (missing) {
            error.value = `Please complete the response for “${
              missing.title
            }” before ${action === 'publish' ? 'publishing' : 'submitting'}.`;
            return;
          }
        }
        if (action === 'save_manager' && meetingDate.value)
          payload.meetingDate = meetingDate.value;
        if (action === 'publish') {
          if (!meetingDate.value || meetingDate.value > today) {
            error.value =
              'Enter the date of the completed review meeting, today or earlier.';
            return;
          }
          payload.meetingDate = meetingDate.value;
          payload.confirmed = true;
        }
      } else if (action === 'acknowledge') {
        if (!review.value.isParticipant || !confirmed.value) return;
        payload.confirmed = true;
        if (acknowledgmentComment.value.trim())
          payload.acknowledgmentComment = acknowledgmentComment.value.trim();
      } else if (action === 'reassign') {
        if (
          !reviewerEmpNumber.value ||
          reviewerEmpNumber.value === review.value.reviewerEmpNumber
        )
          return;
        if (
          !window.confirm(
            'Reassign this review? The previous supervisor will lose access immediately.',
          )
        )
          return;
        payload.reviewerEmpNumber = Number(reviewerEmpNumber.value);
      } else if (action === 'cancel') {
        if (
          !cancelReason.value.trim() ||
          !window.confirm(
            'Cancel this review? The review will remain in history.',
          )
        )
          return;
        payload.reason = cancelReason.value.trim();
      }
      busy.value = true;
      try {
        applyReview(await api.act(review.value.id, payload));
        success.value = {
          save_self: 'Your private draft has been saved.',
          save_manager: 'Your private supervisor draft has been saved.',
          submit_self:
            'Your self review has been submitted to your supervisor.',
          publish: 'The review has been signed and published for the employee.',
          acknowledge: 'Your acknowledgment has been recorded.',
          reassign: 'The supervisor has been reassigned.',
          cancel: 'The review has been cancelled.',
        }[action];
        emit('changed');
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    onMounted(load);
    return {
      review,
      answers,
      meetingDate,
      acknowledgmentComment,
      reviewerEmpNumber,
      cancelReason,
      loading,
      busy,
      error,
      success,
      confirmed,
      dirty,
      today,
      can,
      showManagerAnswers,
      instructions,
      reviewerOptions,
      displayDate,
      statusLabel,
      groupLabel,
      actionLabel,
      load,
      reload,
      close,
      act,
    };
  },
};
</script>
